/**
 * Reset Password Page
 *
 * Password reset completion page where users set their new password
 * after clicking the reset link from their email.
 *
 * @module pages/auth/ResetPasswordPage
 */

import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { verifyPasswordResetCode, confirmPasswordReset, signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from '@/lib/firebase';
import { usePageTitle } from '@/hooks';
import { Button, Input, Alert } from '@/components/ui';
import { ArrowLeft, Eye, EyeOff, CheckCircle2, XCircle, Loader2 } from 'lucide-react';

// =============================================================================
// Types
// =============================================================================

type PageState = 'verifying' | 'invalid' | 'form' | 'success';

// =============================================================================
// Component
// =============================================================================

export function ResetPasswordPage() {
  usePageTitle('Reset Password');

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [pageState, setPageState] = useState<PageState>('verifying');
  const [email, setEmail] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const oobCode = searchParams.get('oobCode');

  // ---------------------------------------------------------------------------
  // Verify the oobCode on mount
  // ---------------------------------------------------------------------------

  useEffect(() => {
    async function verifyCode() {
      if (!oobCode) {
        setPageState('invalid');
        return;
      }

      try {
        // Verify the password reset code is valid and get the associated email
        const userEmail = await verifyPasswordResetCode(auth, oobCode);
        setEmail(userEmail);
        setPageState('form');
      } catch (err) {
        console.error('Invalid or expired reset code:', err);
        setPageState('invalid');
      }
    }

    verifyCode();
  }, [oobCode]);

  // ---------------------------------------------------------------------------
  // Form Validation
  // ---------------------------------------------------------------------------

  const passwordError = password.length > 0 && password.length < 6
    ? 'Password must be at least 6 characters'
    : null;

  const confirmPasswordError = confirmPassword.length > 0 && password !== confirmPassword
    ? 'Passwords do not match'
    : null;

  const isFormValid =
    password.length >= 6 &&
    password === confirmPassword &&
    !loading;

  // ---------------------------------------------------------------------------
  // Handle Password Reset
  // ---------------------------------------------------------------------------

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!oobCode || !isFormValid || !email) return;

    setLoading(true);
    setError(null);

    try {
      // Reset the password
      await confirmPasswordReset(auth, oobCode, password);

      try {
        // Sign in the user with their new password
        await signInWithEmailAndPassword(auth, email, password);

        // Redirect to profile settings with success state
        navigate('/settings/profile', { state: { passwordReset: true }, replace: true });
      } catch (signInErr) {
        // Password reset succeeded but sign-in failed (rare edge case)
        // Show success state and let user sign in manually
        console.error('Sign-in after password reset failed:', signInErr);
        setPageState('success');
      }
    } catch (err) {
      console.error('Password reset failed:', err);
      const firebaseError = err as { code?: string };

      if (firebaseError.code === 'auth/expired-action-code') {
        setError('This reset link has expired. Please request a new one.');
      } else if (firebaseError.code === 'auth/invalid-action-code') {
        setError('This reset link is invalid. Please request a new one.');
      } else if (firebaseError.code === 'auth/weak-password') {
        setError('Password is too weak. Please choose a stronger password.');
      } else {
        setError('Failed to reset password. Please try again.');
      }
      setLoading(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Render States
  // ---------------------------------------------------------------------------

  const renderContent = () => {
    switch (pageState) {
      case 'verifying':
        return (
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-brand-50 mb-4">
              <Loader2 className="h-6 w-6 text-brand-600 animate-spin" />
            </div>
            <h1 className="text-2xl font-display font-semibold text-gray-900 mb-2">
              Verifying reset link...
            </h1>
            <p className="text-gray-500">
              Please wait while we verify your password reset link.
            </p>
          </div>
        );

      case 'invalid':
        return (
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-error-50 mb-4">
              <XCircle className="h-6 w-6 text-error-600" />
            </div>
            <h1 className="text-2xl font-display font-semibold text-gray-900 mb-2">
              Invalid or expired link
            </h1>
            <p className="text-gray-500 mb-6">
              This password reset link is invalid or has expired.
              Please request a new reset link.
            </p>
            <Link to="/forgot-password">
              <Button>Request new link</Button>
            </Link>
            <div className="mt-6">
              <Link
                to="/login"
                className="inline-flex items-center gap-2 text-sm font-medium text-brand-600 hover:text-brand-700"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to sign in
              </Link>
            </div>
          </div>
        );

      case 'success':
        return (
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-success-50 mb-4">
              <CheckCircle2 className="h-6 w-6 text-success-600" />
            </div>
            <h1 className="text-2xl font-display font-semibold text-gray-900 mb-2">
              Password reset successful
            </h1>
            <p className="text-gray-500 mb-6">
              Your password has been reset. Redirecting you to sign in...
            </p>
            <Link
              to="/login"
              className="inline-flex items-center gap-2 text-sm font-medium text-brand-600 hover:text-brand-700"
            >
              <ArrowLeft className="h-4 w-4" />
              Sign in now
            </Link>
          </div>
        );

      case 'form':
        return (
          <>
            {/* Header */}
            <div className="mb-8">
              <h1 className="text-2xl font-display font-semibold text-gray-900">
                Set your new password
              </h1>
              <p className="text-gray-500 mt-1">
                Enter a new password for your account.
              </p>
            </div>

            {/* Error Alert */}
            {error && (
              <Alert variant="error" className="mb-6" onDismiss={() => setError(null)}>
                {error}
              </Alert>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Input
                  label="New password"
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter new password"
                  autoComplete="new-password"
                  autoFocus
                  required
                  error={passwordError || undefined}
                  rightElement={
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  }
                />
              </div>

              <div>
                <Input
                  label="Confirm password"
                  type={showConfirmPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  autoComplete="new-password"
                  required
                  error={confirmPasswordError || undefined}
                  rightElement={
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  }
                />
              </div>

              <Button
                type="submit"
                fullWidth
                loading={loading}
                disabled={!isFormValid}
              >
                Reset password
              </Button>
            </form>

            {/* Back to login */}
            <div className="text-center mt-6">
              <Link
                to="/login"
                className="inline-flex items-center gap-2 text-sm font-medium text-brand-600 hover:text-brand-700"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to sign in
              </Link>
            </div>
          </>
        );
    }
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="min-h-screen flex overflow-auto no-scrollbar">
      {/* Left side - Form */}
      <div className="flex-1 flex flex-col justify-center px-4 sm:px-6 lg:px-8 py-12">
        <div className="w-full max-w-sm mx-auto">
          {/* Logo */}
          <div className="flex items-center gap-2 mb-8">
            <div className="flex items-center justify-center h-10 w-10 rounded-xl bg-brand-500 overflow-hidden">
              <svg className="w-10 h-10" viewBox="0 0 32 32" fill="none">
                <path d="M7 10L13 16L7 22" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" opacity="0.5"/>
                <path d="M13 10L19 16L13 22" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" opacity="0.75"/>
                <path d="M19 10L25 16L19 22" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <span className="font-display font-semibold text-xl text-gray-900">
              Hummingbird
            </span>
          </div>

          {renderContent()}
        </div>
      </div>

      {/* Right side - Decorative */}
      <div className="hidden lg:flex lg:flex-1 bg-gradient-to-br from-brand-500 to-brand-600 relative overflow-hidden">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-10">
          <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" strokeWidth="1"/>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>
        </div>

        {/* Content */}
        <div className="relative flex flex-col justify-center px-12 text-white">
          <h2 className="text-4xl font-display font-semibold mb-4">
            Engineered to an unreasonable standard.
          </h2>
          <p className="text-xl text-white/80 max-w-md">
            We spent years shaving off milliseconds that other platforms ignore. You get the benefit.
          </p>

          {/* Stats */}
          <div className="flex gap-12 mt-12">
            <div>
              <p className="text-4xl font-semibold">&lt;50ms</p>
              <p className="text-white/60">Median latency</p>
            </div>
            <div>
              <p className="text-4xl font-semibold">99.99%</p>
              <p className="text-white/60">Uptime SLA</p>
            </div>
            <div>
              <p className="text-4xl font-semibold">300+</p>
              <p className="text-white/60">Edge locations</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
